/**
 * GEDCOM Tokenizer
 *
 * Parses GEDCOM text line-by-line into flat TreeNode tokens.
 * Handles different line endings and malformed lines gracefully.
 *
 * Part of Phase 4 - Slice 2: Parser Integration
 */

import type { TreeNode } from '../types/TreeNode';

/**
 * GEDCOM line format regex
 * Format: <level> [<pointer>] <tag> [<value>]
 * Example: "0 @I1@ INDI" or "1 NAME John /Doe/" or "2 GIVN John"
 *
 * Captures:
 * 1: level (required)
 * 2: pointer with surrounding whitespace (optional)
 * 3: tag (required)
 * 4: value (optional)
 */
const GEDCOM_LINE_REGEX = /^(\d+)\s+(@[A-Za-z0-9_-]+@\s+)?([A-Z_][A-Z0-9_]*)\s*(.*)?$/;

/**
 * Tokenize GEDCOM text into flat TreeNode array
 *
 * @param text - Raw GEDCOM file text (decoded)
 * @returns Array of TreeNode tokens (flat, not hierarchical yet)
 */
export const tokenizeGedcom = (text: string): TreeNode[] => {
  // Handle different line endings: \r\n (Windows), \n (Unix), \r (Old Mac)
  const lines = text.split(/\r?\n/);
  const tokens: TreeNode[] = [];

  lines.forEach((line, index) => {
    const lineNumber = index + 1;
    const trimmedLine = line.trim();

    // Skip empty lines
    if (trimmedLine === '') {
      return;
    }

    // Try to parse the line
    const match = GEDCOM_LINE_REGEX.exec(trimmedLine);

    if (!match) {
      // Malformed line - log warning and skip
      console.warn(
        `[GEDCOM Parser] Skipping malformed line ${lineNumber}: "${trimmedLine.substring(0, 50)}${
          trimmedLine.length > 50 ? '...' : ''
        }"`
      );
      return;
    }

    // Extract components
    const level = parseInt(match[1], 10);
    const pointerMatch = match[2]; // "@I1@ " or undefined
    const pointer = pointerMatch ? pointerMatch.trim() : null;
    const tag = match[3];
    const value = match[4] && match[4].trim() !== '' ? match[4].trim() : null;

    // Create token
    tokens.push({
      level,
      pointer,
      tag,
      value,
      children: [],
      lineNumber,
    });
  });

  return tokens;
};
